import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';

import useGlobalStatus from '../../hook/useGlobalStatus';

import * as urlConstants from '../../constants/url';
import * as mediaConstants from '../../constants/mediaData';

import { checkAddressesIsNull } from '../formData/formData';

import CopyReport from './CopyReport';
import TransferReport from './TransferReport';
import { HandleSendDate } from './data/sendDetailConstants';

import { handleColorSystem } from '../customComponent/colorSystem';
import { BoxFlexBetweenStart } from '../customComponent/box';
import theme from '../../theme';

import {
  ExpandLess,
  ExpandMore,
  EditOutlinedIcon,
  DeleteOutlineOutlinedIcon,
} from '../../constants/images';


const styleEllipsis = {
  width: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
}

const RenderStatus = ({ status }) => {
  let color;
  let title;

  switch (status) {
    case 'ACTIVE':
      color = theme.palette.success.main;
      title = <FormattedMessage id="sendList.table.status.active" />;
      break;
    case 'PAUSE':
      color = theme.palette.grey.main;
      title = <FormattedMessage id="sendList.table.status.pause" />;
      break;
    case 'PENDING':
      color = theme.palette.warning.main;
      title = <FormattedMessage id="sendList.table.status.pending" />;
      break;
    default:
      break;
  }

  return (
    <Tooltip title={title} >
      <div
        style={{
          width: '8px',
          height: '8px',
          margin: '0 auto',
          backgroundColor: color,
          borderRadius: '16px'
        }}
      />
    </Tooltip>
  )
}

const RenderTdContent = (props) => {
  const {
    media,
    id,
    row,
    deleteData,
    setIsLoadingData,
    setModalName,
    fetchData,
    setPage
  } = props;

  const history = useHistory();
  const { currentActiveData, setCurrentActiveData, locale, setLocale, entryPath, setEntryPath } = useGlobalStatus();
  const location = useLocation();
  const limit = 1;
  const [dropDown, setIsDropDown] = useState(false);
  const { colorSystem, mainColor, lightColor, darkColor, w01Color } = handleColorSystem(media);

  const handlePath = (media, type) => {
    let editPath;
    let createPath;

    if (media === 'FACEBOOK') {
      editPath = urlConstants.PAGE_FB_EDIT_DATA;
      createPath = urlConstants.PAGE_FB_CREATE_DATA;
    } else if (media === 'LINE') {
      editPath = urlConstants.PAGE_LINE_EDIT_DATA;
      createPath = urlConstants.PAGE_LINE_CREATE_DATA;
    } else if (media === 'GOOGLE') {
      editPath = urlConstants.PAGE_GOOGLE_EDIT_DATA;
      createPath = urlConstants.PAGE_GOOGLE_CREATE_DATA;
    } else {
      editPath = urlConstants.PAGE_GOOGLE_EDIT_DATA;
      createPath = urlConstants.PAGE_GOOGLE_CREATE_DATA;
    }

    return type === 'edit' ? editPath : createPath;
  }

  const styleIcons = {
    cursor: 'pointer',
    '&:hover': { color: mainColor }
  }

  switch (id) {
    case 'status':
      return (
        <RenderStatus status={row.status} />
      )
      break;
    case 'weeklyReportName':
      return (
        <Typography
          sx={{
            cursor: 'pointer',
            transition: 'color .1s',
            '&:hover': {
              color: mainColor
            }
          }}
        >
          {row[id]}
        </Typography>
      )
    case 'sendDay':
      return (
        <Typography sx={{ wordWrap: 'normal' }} >
          {checkAddressesIsNull(row.toAddresses) ? null : HandleSendDate(row, 'send-list', locale)}
        </Typography>
      );
    case 'sendTime':
      return (
        <Typography sx={{ wordWrap: 'normal' }} >
          {checkAddressesIsNull(row.toAddresses) ? null : (row.sendTime)}
        </Typography>
      );
    case 'facebookAccounts':
      return (
        <BoxFlexBetweenStart>
          <Box
            sx={{
              width: 'calc(100% - 32px)',
              flex: 1
            }}>
            <Box>
              {
                row.facebookAccounts.slice(0, limit).map(el => {
                  return (
                    <Typography
                      key={el.adAccountId}
                      title={`${el.adAccountId} ${el.adAccountName || ''}`}
                      sx={{ ...styleEllipsis }}
                    >
                      {`${el.adAccountId} ${el.adAccountName || ''}`}
                    </Typography>
                  )
                })
              }
            </Box>

            <Collapse in={dropDown} timeout="auto" unmountOnExit>
              {
                row.facebookAccounts.slice(limit).map(el => {
                  return (
                    <Typography
                      key={el.adAccountId}
                      title={`${el.adAccountId} ${el.adAccountName || ''}`}
                      sx={{ ...styleEllipsis }}
                    >
                      {`${el.adAccountId} ${el.adAccountName || ''}`}
                    </Typography>
                  )
                })
              }
            </Collapse>
          </Box>

          {/* icon */}
          {
            row.facebookAccounts.length > limit &&
            <Box
              onClick={() => setIsDropDown(prev => !prev)}
              sx={{
                color: theme.palette.grey.w600,
                cursor: 'pointer'
              }}
            >
              {dropDown ? <ExpandLess /> : <ExpandMore />}
            </Box>
          }
        </BoxFlexBetweenStart>
      )
    case 'toAddresses':
      return (
        <BoxFlexBetweenStart>
          <Box
            sx={{
              width: 'calc(100% - 32px)',
              flex: 1
            }}>
            <Box>
              {
                //不顯示example@gmail.com
                row.toAddresses.filter((item => item !== "example@gmail.com"))
                  .slice(0, limit).map(el => {
                    return (
                      <Typography
                        key={el}
                        title={el}
                        sx={{ ...styleEllipsis }}
                      >
                        {el}
                      </Typography>
                    )
                  })
              }
            </Box>

            <Collapse in={dropDown} timeout="auto" unmountOnExit>
              {
                row.toAddresses.slice(limit).map(el => {
                  return (
                    <Typography
                      key={el}
                      title={el}
                      sx={{ ...styleEllipsis }}
                    >
                      {el}
                    </Typography>
                  )
                })
              }
            </Collapse>
          </Box>

          {/* icon */}
          {
            row.toAddresses.length > limit &&
            <Box
              onClick={() => setIsDropDown(prev => !prev)}
              sx={{
                height: '20px',
                color: theme.palette.grey.w600,
                cursor: 'pointer'
              }}
            >
              {dropDown ? <ExpandLess /> : <ExpandMore />}
            </Box>
          }
        </BoxFlexBetweenStart>
      );
    case 'editReport':
      return (
        <Tooltip title={<FormattedMessage id="sendList.renderTdContent.editReport" />} placement="bottom">
          <EditOutlinedIcon
            variant="dark"
            sx={{ ...styleIcons, color: mainColor }}
            onClick={() => {
              setCurrentActiveData(row);
              setEntryPath(mediaConstants.MEDIA_DATA[media].editDataUrl);
              history.push(mediaConstants.MEDIA_DATA[media].createDataUrl);
            }}
          />
        </Tooltip>
      );
    case 'copyReport':
      return (
        <CopyReport
          row={row}
          media={media}
          setIsLoadingData={setIsLoadingData}
          setModalName={setModalName}
          fetchData={fetchData}
          setPage={setPage}
        />
      );
    case 'transferReport':
      return (
        <TransferReport
          row={row}
          media={media}
          setIsLoadingData={setIsLoadingData}
          setModalName={setModalName}
          fetchData={fetchData}
          setPage={setPage}
        />
      );
    case 'deleteReport':
      return (
        <Tooltip title={<FormattedMessage id="sendList.renderTdContent.deleteReport" />} placement="bottom">
          <DeleteOutlineOutlinedIcon
            variant="dark"
            sx={{ ...styleIcons }}
            onClick={() => {
              deleteData(row)
            }}
          />
        </Tooltip>
      )
    default:
      return row[id];
  }
}

export default RenderTdContent;